import React, { useState } from 'react';
import { Heart, User, CheckCircle, MapPin } from 'lucide-react';
import { Pagination } from '@tutor/ui';
import {
  getOptimisticWishlistState,
  useTutorWishlistOverlayMap,
  useWishlistTutors,
} from '../hooks/api/useWishlistTutors';
import { useAuthContext, useModal } from '@/contexts';
import type { TutorWishlistData } from '../types/api';
import WishlistCaseSelectionModal from '@/components/Modal/WishlistCaseSelectionModal';
import { useI18n } from '@/i18n';
import { localizeStringList, localizeText } from '@/i18n/options';
import { Link } from '@/i18n/navigation';

const MyLikedTutorsPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { locale } = useI18n();
  const { profile, firebaseUser, loading: isAuthLoading } = useAuthContext();
  const { openLoginModal } = useModal();
  const wishlistOverlayMap = useTutorWishlistOverlayMap();
  const [wishlistTutor, setWishlistTutor] = useState<{ id: string; name: string } | null>(null);
  const isZh = locale === 'zh-CN';

  // Fetch wishlist with pagination - only when authenticated and is parent
  const { data, isLoading, isFetching, error, refetch } = useWishlistTutors({
    page: currentPage,
    limit: 12,
    includeProfile: true,
    enabled: !!firebaseUser && profile?.role === 'parent' && !isAuthLoading,
  });

  const wishlists = Array.isArray(data?.data) ? data.data : [];
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages ?? 1;

  const handleRemoveFromWishlist = (tutorId: string, tutorName: string) => {
    if (wishlistOverlayMap.has(tutorId)) return;
    setWishlistTutor({ id: tutorId, name: tutorName });
  };

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="card p-6 max-w-md text-center">
          <Heart className="h-12 w-12 text-gray-300 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{isZh ? '正在加载你收藏的导师' : 'Loading your liked tutors'}</h2>
          <p className="text-gray-600 text-sm">{isZh ? '正在检查你的账户资料...' : 'Checking your account details...'}</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!firebaseUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="card p-6 max-w-md text-center">
          <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{isZh ? '需要登录' : 'Sign In Required'}</h2>
          <p className="text-gray-600 text-sm mb-4">{isZh ? '请先登录以查看你收藏的导师。' : 'Please log in to view your liked tutors.'}</p>
          <button
            onClick={() => openLoginModal()}
            className="btn-primary w-full"
          >
            {isZh ? '登录' : 'Sign In'}
          </button>
        </div>
      </div>
    );
  }

  // Redirect if not a parent
  if (profile?.role !== 'parent') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="card p-6 max-w-md text-center">
          <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{isZh ? '仅限家长' : 'Parent Only'}</h2>
          <p className="text-gray-600 text-sm mb-4">{isZh ? '此页面仅供家长使用，只有家长可以收藏导师。' : 'This page is for parents only. Only parents can like tutors.'}</p>
          <Link to="/" className="text-primary hover:text-primary-dark font-semibold">
            {isZh ? '返回首页' : 'Back to Home'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-secondary text-white py-20 flex items-center justify-center min-h-[20vh]">
        <div className="container mx-auto px-4 text-center">
          <Heart className="h-12 w-12 mx-auto mb-4 fill-current" />
          <h1 className="text-4xl md:text-5xl font-bold mb-2">{isZh ? '我收藏的导师' : 'My Liked Tutors'}</h1>
          <p className="text-lg opacity-90">{isZh ? '你收藏的导师都在这里' : 'Your favorite tutors all in one place'}</p>
        </div>
      </section>

      {/* Results Section */}
      <div className="container mx-auto px-4 py-12">
        {/* Results Count */}
        <div className="mb-6 flex items-center justify-between">
          <div className="text-gray-600">
            {error ? (
              <div className="flex items-center text-red-600">
                <span>{isZh ? '加载收藏导师失败' : 'Failed to load your liked tutors'}</span>
              </div>
            ) : (
              <span>
                {wishlists.length === 0 && !isLoading
                  ? (isZh ? '还没有收藏的导师' : 'No liked tutors yet')
                  : isZh
                    ? `显示 ${wishlists.length} 位导师`
                    : `Showing ${wishlists.length} tutor${wishlists.length !== 1 ? 's' : ''}`}
              </span>
            )}
          </div>
        </div>

        {/* Empty State */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card p-6 animate-pulse">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="h-3 bg-gray-200 rounded" />
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16 bg-white rounded-lg border border-red-100">
            <Heart className="h-16 w-16 text-red-200 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{isZh ? '无法加载收藏导师' : 'Could not load liked tutors'}</h2>
            <p className="text-gray-600 mb-6">
              {error instanceof Error ? error.message : isZh ? '请再试一次。' : 'Please try again.'}
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="btn-primary inline-block disabled:opacity-60"
            >
              {isFetching ? (isZh ? '重试中...' : 'Retrying...') : isZh ? '重试' : 'Try Again'}
            </button>
          </div>
        ) : wishlists.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{isZh ? '还没有收藏导师' : 'No Liked Tutors Yet'}</h2>
            <p className="text-gray-600 mb-6">{isZh ? '现在开始收藏导师，方便之后比较与挑选' : 'Start liking tutors to save them for later'}</p>
            <Link to="/tutors" className="btn-primary inline-block">
              {isZh ? '浏览导师' : 'Browse Tutors'}
            </Link>
          </div>
        ) : (
          <>
            {/* Tutor Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {wishlists.map((wishlist: TutorWishlistData) => {
                const tutor = wishlist.tutor;
                if (!tutor) return null;
                const avatarUrl = tutor.profilePhotoUrl || tutor.profilePicture;
                const isWishlisted = getOptimisticWishlistState(
                  tutor.id,
                  wishlist.isWishlisted ?? tutor.isWishlisted ?? true,
                  wishlistOverlayMap
                );
                const isPending = wishlistOverlayMap.has(tutor.id);

                return (
                  <div key={wishlist.tutorId} className="card p-6 hover:shadow-lg transition-shadow">
                    {/* Card Header with Heart Button */}
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={tutor.fullName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="h-8 w-8 text-primary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-lg">{tutor.fullName}</h3>
                          {tutor.isFullyVerified && (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          )}
                          {!wishlist.tutorAvailable && (
                            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                              {isZh ? '不可用' : 'Unavailable'}
                            </span>
                          )}
                          <button
                            onClick={() => handleRemoveFromWishlist(tutor.id, tutor.fullName)}
                            disabled={isPending}
                            className={`ml-auto rounded p-1 transition-colors disabled:opacity-60 ${
                              isWishlisted
                                ? 'text-red-500 hover:bg-red-50'
                                : 'text-gray-300 hover:bg-gray-50'
                            }`}
                            title={isWishlisted ? (isZh ? '管理关联个案' : 'Manage linked cases') : (isZh ? '正在保存' : 'Saving changes')}
                            aria-label={isZh ? `管理与 ${tutor.fullName} 关联的个案` : `Manage cases linked to ${tutor.fullName}`}
                          >
                            <Heart className={`h-5 w-5 ${isWishlisted ? 'fill-current' : ''}`} />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {isZh ? '更新于 ' : 'Updated '}
                          {new Date(wishlist.lastLinkedAt).toLocaleDateString(locale === 'zh-CN' ? 'zh-CN' : 'en-US')}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4 border-t border-gray-100 pt-4">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {isZh ? '关联个案' : 'Linked cases'} ({wishlist.linkedCaseCount})
                      </p>
                      <div className="space-y-2">
                        {wishlist.linkedCases.map((linkedCase) => (
                          <div key={linkedCase.id} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="text-sm font-medium text-gray-800">
                                {localizeText(locale, linkedCase.subject)} · {localizeText(locale, linkedCase.level)}
                              </span>
                              <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium capitalize text-gray-600 ring-1 ring-gray-200">
                                {localizeText(locale, linkedCase.status.replaceAll('_', ' '))}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              {linkedCase.tuitionType === 'online'
                                ? (isZh ? '线上' : 'Online')
                                : [linkedCase.district, linkedCase.state ? localizeText(locale, linkedCase.state) : linkedCase.state].filter(Boolean).join(', ') || (isZh ? '未设置地点' : 'Location not set')}
                              {linkedCase.hourlyRate ? ` · RM ${linkedCase.hourlyRate}/${isZh ? '小时' : 'hr'}` : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* School/Major */}
                    {tutor.profileCompletionPercentage !== undefined && (
                      <div className="mb-3 pb-3 border-b border-gray-200">
                        <p className="text-xs text-gray-600">
                          {isZh
                            ? `资料完成度 ${Math.round(tutor.profileCompletionPercentage)}%`
                            : `${Math.round(tutor.profileCompletionPercentage)}% profile complete`}
                        </p>
                      </div>
                    )}

                    {/* Teaching Info */}
                    <div className="space-y-2 text-sm mb-4">
                      {tutor.teachingLanguages && tutor.teachingLanguages.length > 0 && (
                        <div className="flex items-center text-gray-600">
                          <span className="font-medium mr-2">{isZh ? '语言：' : 'Languages:'}</span>
                          {localizeStringList(locale, tutor.teachingLanguages).join(', ')}
                        </div>
                      )}
                      {(tutor.preferredState || (tutor.preferredDistricts && tutor.preferredDistricts.length > 0)) && (
                        <div className="flex items-center text-gray-600">
                          <MapPin className="h-4 w-4 mr-2" />
                          {tutor.preferredDistricts && tutor.preferredDistricts.length > 0
                            ? `${tutor.preferredDistricts.slice(0, 2).join(', ')}${tutor.preferredDistricts.length > 2 ? ` (+${tutor.preferredDistricts.length - 2})` : ''}`
                            : tutor.preferredState ? localizeText(locale, tutor.preferredState) : tutor.preferredState}
                        </div>
                      )}
                    </div>

                    {/* Rate */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="text-primary font-bold text-lg">
                        {tutor.hourlyRate ? `RM ${tutor.hourlyRate}/${isZh ? '小时' : 'hr'}` : (isZh ? '待定' : 'TBD')}
                      </div>
                      <Link to={`/tutor/${tutor.id}?source=direct`} className="btn-primary text-sm py-2">
                        {isZh ? '查看资料' : 'View Profile'}
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>
      <WishlistCaseSelectionModal
        isOpen={Boolean(wishlistTutor)}
        tutorId={wishlistTutor?.id}
        tutorName={wishlistTutor?.name}
        onClose={() => setWishlistTutor(null)}
      />
    </div>
  );
};

export default MyLikedTutorsPage;
